import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Send, Clock, Loader2, CheckCircle2, Flame,
  Instagram, RefreshCw, Eye, Film, XCircle, Rocket, Calendar, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const t = localStorage.getItem('token');
  return { Authorization: `Bearer ${t}` };
};

const daysESAccent = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function formatMatchDate(iso) {
  try {
    const d = new Date(iso);
    return `${daysESAccent[d.getDay() === 0 ? 6 : d.getDay() - 1]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return iso;
  }
}

function PickPublishRow({ pick, onPublished }) {
  const [publishing, setPublishing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [lastResults, setLastResults] = useState(pick.publications || []);

  const published = lastResults.find(p => p.channel === 'telegram_free' && p.status === 'published');
  const pending = lastResults.find(p => p.status === 'pending');

  const publishNow = async () => {
    setPublishing(true);
    try {
      const res = await fetch(`${API_URL}/api/publishing/publish-now/${pick.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const data = await res.json();
      if (data.ok) {
        setLastResults(data.results);
        toast.success(`Publicado: ${data.results.filter(r => r.ok).length}/${data.results.length} canales`);
        onPublished && onPublished();
      } else {
        toast.error('Error al publicar');
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    } finally {
      setPublishing(false);
    }
  };

  const previewImgUrl = `${API_URL}/api/content/pick-card/${pick.id}?format=feed`;
  const previewStoryUrl = `${API_URL}/api/content/pick-card/${pick.id}?format=story`;

  return (
    <Card className={`bg-zinc-900/60 border ${published ? 'border-emerald-600/50' : 'border-white/5'}`} data-testid={`pick-row-${pick.id}`}>
      <CardContent className="py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-zinc-500 uppercase">{pick.league}</span>
              <span className="text-xs text-zinc-600">•</span>
              <span className="text-xs text-zinc-400">{formatMatchDate(pick.match_date)}</span>
              {pick.value_edge > 0 && (
                <Badge className="bg-emerald-600/20 text-emerald-300 text-xs border-0">+{pick.value_edge}% valor</Badge>
              )}
              {published && (
                <Badge className="bg-emerald-600/30 text-emerald-200 text-xs border-0">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Publicado
                </Badge>
              )}
            </div>
            <div className="text-white font-semibold truncate">
              {pick.home_team} <span className="text-zinc-500">vs</span> {pick.away_team}
            </div>
            <div className="text-sm mt-1">
              <span className="text-emerald-400 font-bold">{pick.prediction || pick.market}</span>
              <span className="text-zinc-400 ml-2">@{pick.odds}</span>
              <span className="text-zinc-500 ml-2">• Stake {pick.stake}u</span>
              <span className="text-zinc-500 ml-2">• Confianza {pick.confidence}%</span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewOpen(true)}
              data-testid={`preview-btn-${pick.id}`}
            >
              <Eye className="w-4 h-4 mr-1" /> Preview
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={publishing || !!published}
              onClick={publishNow}
              data-testid={`publish-btn-${pick.id}`}
            >
              {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {published ? 'Ya publicado' : 'Publicar al FREE'}
            </Button>
          </div>
        </div>

        {lastResults && lastResults.length > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap">
            {lastResults.map((r) => (
              <Badge
                key={`${r.channel}-${r.status}-${r.post_url || r.error || 'x'}`}
                className={`text-xs border-0 ${
                  r.status === 'published' ? 'bg-emerald-600/20 text-emerald-300'
                  : r.status === 'failed' ? 'bg-red-600/20 text-red-300'
                  : 'bg-zinc-700/40 text-zinc-300'
                }`}
              >
                {r.channel === 'telegram_free' && '📢 Telegram FREE'}
                {r.channel === 'instagram' && <><Instagram className="w-3 h-3 mr-1 inline" />Instagram</>}
                {r.channel === 'tiktok' && '🎵 TikTok'}
                {' · '}
                {r.status}
                {r.mocked && ' (mock)'}
              </Badge>
            ))}
          </div>
        )}

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-4xl">
            <DialogHeader>
              <DialogTitle className="text-white">Preview · {pick.home_team} vs {pick.away_team}</DialogTitle>
            </DialogHeader>
            <Tabs defaultValue="feed">
              <TabsList>
                <TabsTrigger value="feed">Feed 1:1</TabsTrigger>
                <TabsTrigger value="story">Story 9:16</TabsTrigger>
                <TabsTrigger value="reel">Reel MP4</TabsTrigger>
              </TabsList>
              <TabsContent value="feed" className="pt-3">
                <img
                  src={previewImgUrl}
                  alt="Feed"
                  className="w-full max-w-md mx-auto rounded-lg border border-white/10"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </TabsContent>
              <TabsContent value="story" className="pt-3">
                <img
                  src={previewStoryUrl}
                  alt="Story"
                  className="w-full max-w-xs mx-auto rounded-lg border border-white/10"
                />
              </TabsContent>
              <TabsContent value="reel" className="pt-3">
                <ReelPreview pickId={pick.id} />
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function ReelPreview({ pickId }) {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/publishing/preview/pick-reel/${pickId}`, { headers: authHeader() });
      if (!res.ok) throw new Error('Error generando reel');
      const blob = await res.blob();
      setBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!blobUrl) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-zinc-400 mb-3">Genera un reel MP4 (~10-15s). Tarda ~15s la primera vez.</p>
        <Button onClick={load} disabled={loading} data-testid="generate-reel-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Film className="w-4 h-4 mr-1" />}
          Generar reel
        </Button>
      </div>
    );
  }
  return (
    <video src={blobUrl} controls autoPlay loop className="w-full max-w-xs mx-auto rounded-lg" />
  );
}

function HistoryTab() {
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/publishing/history?limit=100`, { headers: authHeader() });
      const data = await res.json();
      setHistory(data.publications || []);
    } catch (e) {
      console.error('History load failed:', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-zinc-900/60 border-white/5">
      <CardHeader>
        <CardTitle className="text-white flex items-center justify-between">
          <span>Historial de publicaciones</span>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <p className="text-zinc-500 text-sm">Aún no hay publicaciones.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-3 p-2 rounded bg-zinc-900/60 text-sm" data-testid={`history-row-${h.id}`}>
                <div className="min-w-0">
                  <div className="text-white truncate">
                    {h.kind === 'viral' ? `🔥 ${h.snippet_title}` : `🎯 Pick ${(h.pick_id || '').slice(0, 8)}`}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {h.channel} · {new Date(h.created_at).toLocaleString()}
                  </div>
                </div>
                <Badge className={`text-xs border-0 shrink-0 ${
                  h.status === 'published' ? 'bg-emerald-600/20 text-emerald-300'
                  : h.status === 'failed' ? 'bg-red-600/20 text-red-300'
                  : 'bg-zinc-700/40 text-zinc-300'
                }`}>
                  {h.status === 'published' && <CheckCircle2 className="w-3 h-3 mr-1 inline" />}
                  {h.status === 'failed' && <XCircle className="w-3 h-3 mr-1 inline" />}
                  {h.status === 'pending' && <Clock className="w-3 h-3 mr-1 inline" />}
                  {h.status}
                  {h.mocked && ' · mock'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ScheduleTab() {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [planning, setPlanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/publishing/schedule`, { headers: authHeader() });
      const data = await res.json();
      setSchedule(data.schedule || []);
    } catch (e) {
      console.error('Schedule load failed:', e);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const planWeek = async () => {
    if (planning) return;
    setPlanning(true);
    try {
      const res = await fetch(`${API_URL}/api/publishing/schedule-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({}),
      });
      // Use clone before reading to avoid "body is locked" in edge cases
      const safeRes = res.clone();
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = await safeRes.json();
          detail = body.detail || JSON.stringify(body);
        } catch (jsonErr) {
          console.warn('Non-JSON error body:', jsonErr);
          try { detail = (await safeRes.text()).slice(0, 200); } catch (err) { console.warn('Cannot read error text:', err); }
        }
        toast.error(`Error: ${detail}`);
        return;
      }
      const data = await res.json();
      if (data.ok) {
        toast.success(`Pack de 7 días programado: ${data.count} publicaciones`);
        await load();
      } else {
        toast.error('Respuesta inesperada del servidor');
      }
    } catch (e) {
      toast.error(`Error red: ${e.message}`);
    } finally {
      setPlanning(false);
    }
  };

  const removeSlot = async (id) => {
    await fetch(`${API_URL}/api/publishing/schedule/${id}`, { method: 'DELETE', headers: authHeader() });
    await load();
  };

  // Group by date (CET)
  const byDate = {};
  schedule.forEach((s) => {
    const d = new Date(s.scheduled_for);
    const key = d.toLocaleDateString('es-ES', {
      weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'Europe/Madrid'
    });
    (byDate[key] = byDate[key] || []).push(s);
  });

  const kindIcon = { pick: '🎯', viral: '🔥', results: '📊' };
  const kindLabel = { pick: 'Pick del día', viral: 'Contenido viral', results: 'Resultados' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-zinc-400">
            3 publicaciones/día (09:00 · 14:00 · 22:00 CET) durante 7 días. La IA asigna
            el mejor pick por día — puedes cambiar cualquier slot.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700"
            onClick={planWeek}
            disabled={planning}
            data-testid="plan-week-btn"
          >
            {planning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calendar className="w-4 h-4 mr-1" />}
            Programar pack de 7 días
          </Button>
        </div>
      </div>

      {Object.keys(byDate).length === 0 ? (
        <Card className="bg-zinc-900/60 border-white/5">
          <CardContent className="py-10 text-center text-zinc-400">
            No hay publicaciones programadas. Haz clic en "Programar pack de 7 días" para planificar la semana.
          </CardContent>
        </Card>
      ) : (
        Object.entries(byDate).map(([date, items]) => (
          <Card key={date} className="bg-zinc-900/60 border-white/5" data-testid={`schedule-day-${date}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm uppercase tracking-wider text-amber-300">
                {date}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {items.map((s) => {
                const t = new Date(s.scheduled_for).toLocaleTimeString('es-ES', {
                  hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid'
                });
                return (
                  <div key={s.id} className="flex items-center gap-3 p-3 bg-zinc-950/40 rounded border border-white/5">
                    <div className="text-xs font-mono text-zinc-400 shrink-0 w-14">{t}</div>
                    <div className="text-lg shrink-0">{kindIcon[s.kind]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm">
                        {kindLabel[s.kind]}
                        {s.pick_summary && (
                          <span className="text-zinc-400 ml-2">
                            · {s.pick_summary.home_team} vs {s.pick_summary.away_team}
                            <span className="text-emerald-400 ml-2">{s.pick_summary.prediction} @{s.pick_summary.odds}</span>
                          </span>
                        )}
                        {s.kind === 'viral' && s.snippet?.title && (
                          <span className="text-zinc-400 ml-2">· {s.snippet.title}</span>
                        )}
                      </div>
                      {s.error && (
                        <div className="text-xs text-amber-400 mt-1">⚠ {s.error}</div>
                      )}
                    </div>
                    <Badge className={`text-xs border-0 shrink-0 ${
                      s.status === 'pending' ? 'bg-emerald-600/20 text-emerald-300'
                      : s.status === 'skipped' ? 'bg-zinc-700/40 text-zinc-400'
                      : 'bg-red-600/20 text-red-300'
                    }`}>
                      {s.status === 'pending' ? 'programado' : s.status}
                    </Badge>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => removeSlot(s.id)}
                      className="text-zinc-500 hover:text-red-400"
                      data-testid={`delete-slot-${s.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

export default function Publishing() {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadPicks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/publishing/daily-picks?days_ahead=3`, { headers: authHeader() });
      if (!res.ok) throw new Error('Error');
      const data = await res.json();
      setPicks(data.picks || []);
    } catch (e) {
      toast.error('Error cargando picks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPicks(); }, [loadPicks]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 pt-20 pb-10 space-y-5" data-testid="publishing-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-white">📱 Publicación automática</h1>
            <p className="text-sm text-zinc-400 mt-1">
              Elige qué picks mandar al canal FREE. El sistema publica en Telegram, Instagram y TikTok en un click.
            </p>
          </div>
          <Button variant="outline" onClick={loadPicks} disabled={loading} data-testid="refresh-picks-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recargar
          </Button>
        </div>

        <Tabs defaultValue="picks" className="w-full">
          <TabsList>
            <TabsTrigger value="picks" data-testid="tab-picks">Picks del día</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">Calendario semanal</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Historial</TabsTrigger>
          </TabsList>

          <TabsContent value="picks" className="space-y-3 pt-4">
            {loading && picks.length === 0 ? (
              <div className="text-center py-10">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
              </div>
            ) : picks.length === 0 ? (
              <Card className="bg-zinc-900/60 border-white/5">
                <CardContent className="py-10 text-center text-zinc-400">
                  No hay picks pendientes en los próximos 3 días.
                </CardContent>
              </Card>
            ) : (
              picks.map((p) => (
                <PickPublishRow key={p.id} pick={p} onPublished={loadPicks} />
              ))
            )}
          </TabsContent>

          <TabsContent value="schedule" className="pt-4">
            <ScheduleTab />
          </TabsContent>

          <TabsContent value="history" className="pt-4">
            <HistoryTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
